This program has been developed by students from the bachelor Computer Science
at Utrecht University within the Software Project course. Copyright (c) Utrecht
University (Department of Information and Computing Sciences)

<template>
  <div>
    <div class="basicBlock">
      <p class="header upper" v-if="this.public === false">
        {{ $t("lessonPackages.myBundles") }}
      </p>
      <p class="header upper" v-else>
        {{ $t("lessonPackages.publicBundles") }}
      </p>
      <div>
        <div
          class="blockEntry grid-cols-5 selectable buttonsRight leftRight"
          v-for="(b, i) in this.bundles"
          :class="[
            b?.bundleId === selected?.bundleId ? 'selected' : 'selectable',
          ]"
          :key="b.bundleId"
          @click="changeSelectedBundle(b, b.name, b.lock)"
        >
          <p>{{ b.name }}</p>
          <div :class="this.moreDisplay[i]">
            <!-- Remove bundle -->
            <div class="tooltip">
              <button
                class="button small"
                :class="[b.published ? 'grey' : 'warning']"
              >
                <img
                  src="@/assets/images/icons/Trash.svg"
                  @click="removeBundle(b.bundleId)"
                  alt="Remove bundle icon"
                />
              </button>
              <div class="tooltipText top-5">
                <p v-if="!b.published">
                  {{ $t("lessonPackages.tooltips.removeBundle") }}
                </p>
                <p v-else>{{ $t("lessonPackages.removePublic") }}</p>
                <i></i>
              </div>
            </div>
            <!-- Publish bundle -->
            <div class="tooltip">
              <button
                class="button small"
                :class="[b.published ? 'grey' : 'success']"
              >
                <img
                  v-if="
                    getUserInfo().permissionLevel == 'admin' && !b.published
                  "
                  src="@/assets/images/icons/Publish.svg"
                  @click="errorVisible = 'overlayVisible'"
                  alt="Publish bundle icon"
                />
                <img
                  v-else-if="!b.published"
                  src="@/assets/images/icons/Publish.svg"
                  @click="requestPublication(b.bundleId)"
                  alt="Request publication icon"
                />
                <img
                  v-else
                  src="@/assets/images/icons/Publish.svg"
                  alt="Already published icon"
                />
              </button>
              <div class="tooltipText top-5">
                <p
                  v-if="
                    getUserInfo().permissionLevel == 'admin' && !b.published
                  "
                >
                  {{ $t("lessonPackages.tooltips.publishBundle") }}
                </p>
                <p v-else-if="!b.published">
                  {{ $t("lessonPackages.tooltips.publicationBundle") }}
                </p>
                <p v-else>{{ $t("lessonPackages.tooltips.alreadyPublic") }}</p>
                <i></i>
              </div>
              <div class="overlay" :class="errorVisible">
                <div class="message">
                  <div class="messageHeader error">
                    {{ $t("lessonPackages.publishBundle") }}
                  </div>
                  <p>
                    {{ $t("lessonPackages.publishWarning") }}
                  </p>
                  <div class="buttonsCenter">
                    <button class="button warning" @click="errorVisible = ''">
                      {{ $t("lessonPackages.cancel") }}
                    </button>
                    <button
                      class="button success"
                      @click="publishBundle(b.bundleId)"
                    >
                      {{ $t("lessonPackages.publish") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- Clone bundle -->
            <div class="tooltip">
              <button
                class="button small success"
                @click="cloneBundle(b.bundleId, b.name, b.lock)"
              >
                <img
                  src="@/assets/images/icons/Duplicate.svg"
                  alt="Duplicate bundle icon"
                />
              </button>
              <div class="tooltipText top-5">
                <p>{{ $t("lessonPackages.tooltips.cloneBundle") }}</p>
                <i></i>
              </div>
            </div>
          </div>
          <img
            class="more"
            src="@/assets/images/icons/More.svg"
            @click="toggleMoreDisplay(i)"
            alt="More buttons icon"
          />
        </div>
      </div>
      <div class="buttonsCenter spaceAbove">
        <button class="button success" @click="showMore(!this.public)">
          <p v-if="this.public === false">
            {{ $t("lessonPackages.showPublic") }}
          </p>
          <p v-else>{{ $t("lessonPackages.myBundles") }}</p>
        </button>
      </div>
      <input
        id="nameBundle"
        class="input"
        :placeholder="$t('lessonPackages.newName')"
        v-model="nameBundle"
        autocomplete="off"
      />
      <div class="buttonsCenter spaceAbove">
        <button class="button success" @click="createNew(nameBundle)">
          {{ $t("lessonPackages.newPackage") }}
        </button>
        <div class="overlay" :class="{ overlayVisible: overlay }">
          <div class="message">
            <p class="messageHeader error">Error</p>
            <p>{{ this.updateMessage }}</p>
            <div class="buttonsCenter">
              <button class="button success" @click="overlay = false">
                {{ $t("lessonPackages.ok") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { bundleAPI } from "@/utils/apiCalls";
import { bundle } from "@/utils/Types";
import { getUserInfo } from "@/utils/Functions";

@Options({
  data() {
    return {
      selected: null,
      public: false,
      bundles: [],
      publicBundles: [],
      publicDisplay: [],
      ownBundles: [],
      ownDisplay: [],
      overlay: false,
      moreDisplay: [],
      nameBundle: "",
      updateMessage: "",
      messageTitle: "",
      errorVisible: "",
    };
  },
  methods: {
    showMore(toggle: boolean) {
      this.public = toggle;
      if (toggle) {
        this.moreDisplay = this.publicDisplay;
        this.bundles = this.publicBundles;
      } else {
        this.moreDisplay = this.ownDisplay;
        this.bundles = this.ownBundles;
      }
    },
    /**
     * Remove bundle
     * @param id Bundle id
     */
    async removeBundle(id: number) {
      await bundleAPI.deleteBundle(id).then(async (err) => {
        if (err === null) {
          this.overlay = true;
          this.messageTitle = "Error";
          this.updateMessage = this.$t("general.error.lessonPackage.inUse");
        } else {
          await this.changeSelectedBundle("");
          await this.updateMyBundles();
          this.$emit("bundleRemoved");
        }
      });
    },
    /**
     * Request publication of bundle
     * @param id Bundle id
     */
    async requestPublication(id: number) {
      await bundleAPI.requestPublicationBundle(id);
    },
    /**
     * Publish bundle
     * @param id Bundle id
     */
    async publishBundle(id: number) {
      await bundleAPI.publishBundle(id).then(() => {
        this.updateMyBundles();
      });
    },
    /**
     * Clone bundle
     * @param id Bundle id
     */
    cloneBundle(id: number) {
      bundleAPI.cloneBundle(id).then((value) => {
        this.updateMyBundles();
        this.changeSelectedBundle(value);
      });
    },
    /**
     * Create new bundle
     * @param name Name of new bundle
     */
    async createNew(name: string) {
      if (name != "" && name.length <= 35) {
        bundleAPI.createBundle(name.toString()).then(async (value) => {
          this.updateMyBundles().then(() => {
            this.changeSelectedBundle(value);
          });
        });
        this.nameBundle = "";
      } else if (name.length > 35) {
        this.overlay = true;
        this.messageTitle = "Error";
        this.updateMessage = this.$t("general.error.lessonPackage.tooLong");
      } else {
        this.overlay = true;
        this.messageTitle = "Error";
        this.updateMessage = this.$t("general.error.lessonPackage.noName");
      }
    },
    //Update visible bundles
    async updateMyBundles() {
      await bundleAPI.getMyBundlesTeacher().then((value) => {
        this.ownBundles = value;
        this.bundles = this.ownBundles;
        this.moreDisplay = Array(this.bundles.length).fill("noMoreDisplay");
        this.ownDisplay = Array(this.bundles.length).fill("noMoreDisplay");
        this.public = false;
      });
      if (this.$store.getters.getUserInfo.permissionLevel == "admin") {
        await bundleAPI.getAddableBundles().then((value) => {
          this.publicBundles = value;
          this.publicDisplay = Array(this.publicBundles.length).fill(
            "noMoreDisplay"
          );
        });
      } else {
        await bundleAPI.getPublishedBundles().then((value) => {
          this.publicBundles = value;
          this.publicDisplay = Array(this.publicBundles.length).fill(
            "noMoreDisplay"
          );
        });
      }
    },
    /**
     * Change the selected bundle
     * @param b Bundle
     */
    changeSelectedBundle(b: bundle) {
      this.selected = b;
      this.$emit("updateBundle", b);
    },
    /**
     * Toggle visibilty bundle option buttons
     * @param i Number of bundle in the list
     */
    toggleMoreDisplay(i: number) {
      if (this.moreDisplay[i] === "noMoreDisplay") {
        this.moreDisplay[i] = "moreDisplay";
      } else {
        this.moreDisplay[i] = "noMoreDisplay";
      }
    },
    getUserInfo,
  },
  mounted() {
    this.updateMyBundles();
  },
  props: ["title"],
})
export default class ActiveBundlesBlock extends Vue {}
</script>
